import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import DataTable from 'react-data-table-component';
const apiUrl = process.env.REACT_APP_API_URL;

export default function ProductImportPage() {
  const navigate = useNavigate();
  const [importData, setImportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [importStatus, setImportStatus] = useState('');
  const fileInputRef = React.useRef(null);

  const breadcrumbPaths = [
    { label: 'Products', to: '/products' },
    { label: 'Products List', to: '/products/list' },
    { label: 'Import' }
  ];

  const tableColumns = [
    { name: 'Row', selector: (row, index) => index + 1, sortable: false, width: '60px' },
    { name: 'Name', selector: row => row.name || '', sortable: false },
    { name: 'Cost Price', selector: row => row.cost_price || '', sortable: false },
    { name: 'Tax', selector: row => row.tax || '', sortable: false },
    { name: 'Unit', selector: row => row.unit || '', sortable: false },
    { name: 'Manufacturer', selector: row => row.manufacturer || '', sortable: false },
    { name: 'Category', selector: row => row.category || '', sortable: false },
    { name: 'Status', 
      selector: row => row._validation_status || 'Valid', 
      sortable: false,
      cell: row => (
        <span className={`px-2 py-1 text-xs rounded ${
          row._validation_status === 'Error' 
            ? 'bg-red-100 text-red-800' 
            : 'bg-green-100 text-green-800'
        }`}>
          {row._validation_status || 'Valid'}
        </span>
      )
    }
  ];

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let data = [];
        
        if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
          // Handle CSV
          const text = e.target.result;
          const lines = text.split('\n');
          const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
          
          for (let i = 1; i < lines.length; i++) {
            if (lines[i].trim()) {
              const values = lines[i].split(',');
              const row = {};
              headers.forEach((header, index) => {
                row[header] = values[index]?.trim() || '';
              });
              data.push(row);
            }
          }
        } else {
          // Handle Excel
          const workbook = XLSX.read(e.target.result, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (data.length > 0) {
            const headers = data[0].map(h => String(h).toLowerCase().trim());
            const rows = data.slice(1).map(row => {
              const obj = {};
              headers.forEach((header, index) => {
                obj[header] = row[index] || '';
              });
              return obj;
            }).filter(row => Object.values(row).some(val => val !== ''));
            data = rows;
          }
        }

        // Validate data
        const validatedData = validateImportData(data);
        setImportData(validatedData);
        setImportStatus(`Loaded ${validatedData.length} rows`);
        
      } catch (error) {
        console.error('Error parsing file:', error);
        setImportStatus('Error parsing file. Please check the file format.');
      }
    };

    if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
  };

  const validateImportData = (data) => {
    const errors = [];
    
    return data.map((row, index) => {
      const rowErrors = [];
      
      // Check required fields
      if (!row.name || row.name.trim() === '') {
        rowErrors.push('Name is required');
      }
      
      if (row.price && isNaN(parseFloat(row.price))) {
        rowErrors.push('Price must be a number');
      }
      
      if (row.cost_price && isNaN(parseFloat(row.cost_price))) {
        rowErrors.push('Cost price must be a number');
      }
      
      if (row.tax && (isNaN(parseFloat(row.tax)) || parseFloat(row.tax) < 0 || parseFloat(row.tax) > 100)) {
        rowErrors.push('Tax must be a number between 0 and 100');
      }

      if (rowErrors.length > 0) {
        errors.push({ row: index + 1, errors: rowErrors });
        return { ...row, _validation_status: 'Error', _validation_errors: rowErrors };
      }
      
      return { ...row, _validation_status: 'Valid' };
    });
  };

  const handleImport = async () => {
    if (importData.length === 0) {
      setImportStatus('No data to import');
      return;
    }

    const validRows = importData.filter(row => row._validation_status === 'Valid');
    if (validRows.length === 0) {
      setImportStatus('No valid rows to import');
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/products/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ products: validRows })
      });

      if (response.ok) {
        const result = await response.json();
        setImportStatus({
          message: `Successfully imported ${result.imported} products${result.errors ? `. ${result.errors.length} rows had errors.` : ''}`,
          isError: result.success === false
        });
        if (result.success) {
          setTimeout(() => navigate('/products'), 2000);
        }
      } else {
        const error = await response.json();
        setImportStatus({
          message: `Import failed: ${error.message}`,
          isError: true
        });
      }
    } catch (error) {
      console.error('Import error:', error);
      setImportStatus('Import failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setImportData([]);
    setImportStatus('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const templateData = [
      ['name', 'cost_price', 'tax', 'unit', 'manufacturer', 'category'],
      ['Sample Product', '75.00', '18', 'pcs', 'Sample Manufacturer', 'Sample Category']
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Products');
    XLSX.writeFile(wb, 'products_template.xlsx');
  };

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Import Products</h1>
        </div>

        {/* File Upload Section */}
        <div className="mb-6 p-4 border-2 border-dashed border-gray-300 rounded-lg">
          <div className="text-center">
            <div className="mb-4">
              <label htmlFor="file-upload" className="cursor-pointer">
                <div className="mx-auto w-12 h-12 text-gray-400 mb-4">
                  📁
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium text-indigo-600 hover:text-indigo-500">
                    Click to upload
                  </span>{' '}
                  or drag and drop
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  CSV or Excel files (XLS, XLSX)
                </p>
              </label>
              <input
                ref={fileInputRef}
                id="file-upload"
                name="file-upload"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileUpload}
                className="sr-only"
              />
            </div>
            
            <div className="flex justify-center gap-4">
              <button
                onClick={downloadTemplate}
                className="px-4 py-2 text-sm text-indigo-600 hover:text-indigo-800 border border-indigo-600 rounded"
              >
                Download Template
              </button>
            </div>
          </div>
        </div>

        {/* Status Message */}
        {importStatus && (
          typeof importStatus === 'string' ? (
            <div className="mb-4 p-3 rounded bg-green-100 text-green-700">
              {importStatus}
            </div>
          ) : (
            <div className={`mb-4 p-3 rounded ${
              importStatus.isError
                ? 'bg-red-100 text-red-700'
                : 'bg-green-100 text-green-700'
            }`}>
              {importStatus.message}
            </div>
          )
        )}

        {/* Data Preview Table */}
        {importData.length > 0 && (
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Data Preview</h2>
              <div className="flex gap-2">
                <button
                  onClick={handleClear}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 border rounded"
                >
                  Clear
                </button>
                <button
                  onClick={handleImport}
                  disabled={isLoading || importData.filter(row => row._validation_status === 'Valid').length === 0}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Importing...' : `Import ${importData.filter(row => row._validation_status === 'Valid').length} Products`}
                </button>
              </div>
            </div>
            
            <DataTable
              columns={tableColumns}
              data={importData}
              pagination
              highlightOnHover
              striped
              responsive
              noDataComponent="No data to preview"
            />
          </div>
        )}

        {/* Instructions */}
        <div className="bg-gray-50 rounded p-4">
          <h3 className="font-semibold mb-2">Import Instructions:</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Upload a CSV or Excel file containing product data</li>
            <li>• Required columns: name</li>
            <li>• Optional columns: cost_price, tax, unit, manufacturer, category</li>
            <li>• Download the template file for proper format</li>
            <li>• Review the data preview before importing</li>
            <li>• Only valid rows will be imported</li>
          </ul>
        </div>
      </div>
    </div>
  );
}